import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlay,
    faPause,
    faBackwardStep,
    faForwardStep,
    faFlag,
    faThumbsUp,
    faXmark,
    faCheck,
    faCircleCheck,
    faRotateLeft,
    faLock,
    faHand,
    faFaceFrown,
    faFaceMeh,
    faFaceSadTear,
    faCaretUp,
    faCaretDown,
    faComment,
    faCloudArrowUp,
    faFilm,
    faWandMagicSparkles,
    faCircle,
    faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from './utils/axiosinstance';
import { getCookie } from './utils/csrf';

// ---------------------------------------------------------------------------
// Video annotation tab.
//
// Designed to be usable by people who do not read English: every action is a
// large icon button (English tooltips only as a secondary aid), the timeline
// shows good time in green and bad time in red, and status is color + icon
// coded. Numbers (times) are the only required text.
//
// Verdict model: a video is GOOD by default. Pressing the flag starts marking
// a bad section (the good light goes out); pressing the flag again — or
// pressing good, which cancels — ends the marking state. ✓ Done records the
// review and jumps to the next video; ↺ resets the clip.
//
// Processed videos play side by side with their source video (A/B style,
// kept in sync), like the local DPO review apps.
// ---------------------------------------------------------------------------

const DEFAULT_FPS = 16;

const formatTime = (t) => {
    if (!isFinite(t) || t === null) return '0:00.0';
    const m = Math.floor(t / 60);
    const s = (t - m * 60).toFixed(1).padStart(4, '0');
    return `${m}:${s}`;
};

// Ranges are [start, end, type, severity]: type "hands" | "face" (legacy
// 2-item ranges count as untyped), severity 1 (mild) / 2 (default) / 3
// (severe). Merge overlapping/touching ranges per type+severity.
const rangeType = (r) => r[2] || 'bad';
const rangeSeverity = (r) => r[3] || 2;
const mergeRanges = (ranges) => {
    const key = (r) => `${rangeType(r)}:${rangeSeverity(r)}`;
    const out = [];
    for (const k of [...new Set(ranges.map(key))]) {
        const sorted = ranges.filter((r) => key(r) === k)
                             .sort((a, b) => a[0] - b[0]);
        let acc = null;
        for (const r of sorted) {
            if (acc && r[0] <= acc[1] + 0.01) {
                acc[1] = Math.max(acc[1], r[1]);
            } else {
                acc = [...r];
                out.push(acc);
            }
        }
    }
    return out.sort((a, b) => a[0] - b[0]);
};

// severity -> color, for timeline blocks / chips / the flag buttons
const SEV_BLOCK = { 1: 'bg-sil-orange-500', 2: 'bg-red-500', 3: 'bg-red-900' };
const SEV_BUTTON = {
    1: 'bg-sil-orange-500 bg-opacity-90 hover:bg-opacity-100',
    2: 'bg-red-500 bg-opacity-70 hover:bg-opacity-100',
    3: 'bg-red-900 hover:bg-red-800',
};
const SEV_MARKING = { 1: 'bg-sil-orange-500', 2: 'bg-red-600', 3: 'bg-red-900' };
const SEV_CHIP = {
    1: 'bg-sil-orange-500 bg-opacity-30 border-sil-orange-500',
    2: 'bg-red-500 bg-opacity-30 border-red-500',
    3: 'bg-red-900 bg-opacity-50 border-red-900',
};
// graded face icons: slightly bad / bad / very bad
const FACE_ICONS = { 1: faFaceMeh, 2: faFaceFrown, 3: faFaceSadTear };

const SPEEDS = [
    { rate: 0.25, emoji: '🐢' },
    { rate: 0.5, emoji: '🚶' },
    { rate: 1, emoji: '🐇' },
];

// Review-list order:
//   1. videos this reviewer hasn't marked done (✓) before done ones;
//   2. within each, videos added after the curated launch set newest-first;
//   3. then the curated launch set grouped by name (numeric-aware, so
//      seed42 < seed123 < seed2024 sit together in order).
const CURATED_SET_CUTOFF = Date.parse('2026-10-08T00:00:00Z');
const nameCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

const sortReviewList = (list) => {
    const added = (v) => Date.parse(v.created_at) || 0;
    const done = (v) => (v.annotation && v.annotation.reviewed ? 1 : 0);
    return [...list].sort((a, b) => {
        if (done(a) !== done(b)) return done(a) - done(b);
        const aNew = added(a) >= CURATED_SET_CUTOFF;
        const bNew = added(b) >= CURATED_SET_CUTOFF;
        if (aNew !== bNew) return aNew ? -1 : 1;
        if (aNew) return added(b) - added(a);
        return nameCollator.compare(a.filename, b.filename);
    });
};

// readOnly + annotatorId: the admin Results view — shows the chosen user's
// list and annotations exactly as they see them, with all editing disabled.
const AnnotationTab = ({ readOnly = false, annotatorId = null }) => {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentKey, setCurrentKey] = useState(null); // `${kind}-${id}`
    const [badRanges, setBadRanges] = useState([]);
    const [reviewed, setReviewed] = useState(false);
    const [comment, setComment] = useState('');
    // in-progress section starts, one per category — both can run at once
    const [pending, setPending] = useState({ hands: null, face: null });
    // current badness level per category: 1 mild / 2 default / 3 severe
    const [severity, setSeverity] = useState({ hands: 2, face: 2 });
    const [isPlaying, setIsPlaying] = useState(false);
    const [rate, setRate] = useState(1);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error

    const videoRef = useRef(null);   // output / primary video (drives the clock)
    const srcRef = useRef(null);     // original source video (A/B companion)
    const timelineRef = useRef(null);
    const saveTimer = useRef(null);
    const wantPlay = useRef(false);  // user intent, survives buffering pauses
    const stallPause = useRef(false);

    const current = useMemo(
        () => videos.find((v) => `${v.kind}-${v.id}` === currentKey) || null,
        [videos, currentKey]
    );
    // display order; re-sorts live as videos are marked done
    const sortedVideos = useMemo(() => sortReviewList(videos), [videos]);
    const fps = (current && current.fps) || DEFAULT_FPS;
    const marking = pending.hands !== null || pending.face !== null;
    // Prefer the pre-rendered side-by-side (one file, sync by construction);
    // fall back to synchronized dual playback when it doesn't exist.
    const isSxs = !!(current && current.sxs_url);
    const isPair = !isSxs && !!(current && current.source_url);

    const players = () => [videoRef.current, srcRef.current].filter(Boolean);
    const playAll = () => players().forEach((p) => p.play().catch(() => {}));
    const pauseAll = () => players().forEach((p) => p.pause());

    // Dual-playback babysitting: if either video stalls to buffer, stop both;
    // when everyone can play again, resume (only if the user wanted playback).
    const handleStall = () => {
        if (wantPlay.current) {
            stallPause.current = true;
            pauseAll();
        }
    };
    const handleReady = () => {
        if (wantPlay.current && players().every((p) => p.readyState >= 3)) {
            stallPause.current = false;
            playAll();
        }
    };

    // ------------------------------------------------------------- loading
    useEffect(() => {
        const fetchVideos = async () => {
            try {
                const response = annotatorId
                    ? await axiosInstance.get('/api/admin/annotation_videos/',
                                              { params: { user: annotatorId } })
                    : await axiosInstance.get('/api/fetch_annotation_videos/');
                const list = response.data.videos || [];
                setVideos(list);
                const ordered = sortReviewList(list);
                if (ordered.length > 0) {
                    selectVideo(ordered[0]);
                }
            } catch (error) {
                console.error('Error fetching annotation videos:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchVideos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectVideo = (video) => {
        // save any in-flight change to the OUTGOING video before switching —
        // clicking another video must never discard unsaved marks
        flushPendingSave();
        wantPlay.current = false;
        setCurrentKey(`${video.kind}-${video.id}`);
        const ann = video.annotation;
        setBadRanges(ann && ann.bad_ranges ? ann.bad_ranges : []);
        setReviewed(!!(ann && ann.reviewed));
        setComment((ann && ann.comment) || '');
        setPending({ hands: null, face: null });
        setSeverity({ hands: 2, face: 2 });
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(video.duration || 0);
        setSaveState('idle');
    };

    // ------------------------------------------------------------- saving
    // pendingSave holds the newest unsaved change, PINNED to the video it
    // belongs to — so a flush during a video switch saves to the right record.
    const pendingSave = useRef(null); // {kind, id, fields} | null

    const doPersist = useCallback(
        async (kind, id, fields, { keepalive = false } = {}) => {
            const body = {
                kind,
                video_id: id,
                bad_ranges: fields.badRanges,
                reviewed: fields.reviewed,
                comment: fields.comment,
                duration: fields.duration,
            };
            setSaveState('saving');
            try {
                let saved;
                if (keepalive) {
                    // page is closing/unloading: a keepalive fetch survives it
                    const resp = await fetch('/api/save_annotation/', {
                        method: 'POST',
                        keepalive: true,
                        credentials: 'include',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-CSRFToken': getCookie('csrftoken')
                                || localStorage.getItem('csrfToken') || '',
                        },
                        body: JSON.stringify(body),
                    });
                    saved = (await resp.json()).annotation;
                } else {
                    const response = await axiosInstance.post('/api/save_annotation/', body);
                    saved = response.data.annotation;
                }
                setSaveState('saved');
                setVideos((prev) =>
                    prev.map((v) =>
                        v.kind === kind && v.id === id ? { ...v, annotation: saved } : v
                    )
                );
            } catch (error) {
                console.error('Error saving annotation:', error);
                setSaveState('error');
            }
        },
        []
    );

    // Save any not-yet-persisted change NOW. Called when the debounce fires,
    // and from every exit path (video switch, tab unmount, page close) so a
    // change is never lost by navigating away before the ✓ button.
    const flushPendingSave = useCallback(
        (opts) => {
            clearTimeout(saveTimer.current);
            const p = pendingSave.current;
            pendingSave.current = null;
            if (p) doPersist(p.kind, p.id, p.fields, opts);
        },
        [doPersist]
    );

    // Apply an annotation change and auto-save it (debounced) — no save button.
    const updateAnnotation = useCallback(
        (patch) => {
            if (!current || readOnly) return;
            const next = {
                badRanges, reviewed, comment,
                duration: videoRef.current ? videoRef.current.duration : current.duration,
                ...patch,
            };
            setBadRanges(next.badRanges);
            setReviewed(next.reviewed);
            setComment(next.comment);
            pendingSave.current = { kind: current.kind, id: current.id, fields: next };
            clearTimeout(saveTimer.current);
            saveTimer.current = setTimeout(() => flushPendingSave(), 600);
        },
        [flushPendingSave, current, badRanges, reviewed, comment, readOnly]
    );

    // Flush on page close/refresh and when the tab unmounts (e.g. switching
    // to the Create tab).
    useEffect(() => {
        const onHide = () => flushPendingSave({ keepalive: true });
        window.addEventListener('pagehide', onHide);
        return () => {
            window.removeEventListener('pagehide', onHide);
            flushPendingSave({ keepalive: true });
        };
    }, [flushPendingSave]);

    // ------------------------------------------------------------ playback
    const togglePlay = () => {
        if (!videoRef.current) return;
        if (wantPlay.current) {
            wantPlay.current = false;
            pauseAll();
        } else {
            wantPlay.current = true;
            playAll();
        }
    };

    const seekAll = (t) => {
        players().forEach((p) => {
            p.currentTime = t;
        });
    };

    const step = (n) => {
        const vid = videoRef.current;
        if (!vid) return;
        wantPlay.current = false;
        pauseAll();
        // land mid-frame so browsers never round back to the previous frame
        const f = Math.max(0, Math.floor(vid.currentTime * fps + 0.001) + n);
        const last = Math.max(0, Math.floor((vid.duration || 0) * fps) - 1);
        seekAll((Math.min(f, last) + 0.5) / fps);
    };

    const applyRate = (r) => {
        setRate(r);
        players().forEach((p) => {
            p.playbackRate = r;
        });
    };

    const seekToFraction = (clientX) => {
        const vid = videoRef.current;
        const bar = timelineRef.current;
        if (!vid || !bar || !vid.duration) return;
        const rect = bar.getBoundingClientRect();
        const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        seekAll(frac * vid.duration);
    };

    const handleTimelinePointer = (e) => {
        e.preventDefault();
        seekToFraction(e.clientX);
        const move = (ev) => seekToFraction(ev.clientX);
        const up = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
    };

    // keep the companion video locked to the primary one
    const handleTimeUpdate = (e) => {
        const t = e.target.currentTime;
        setCurrentTime(t);
        const src = srcRef.current;
        if (src && Math.abs(src.currentTime - t) > 0.1) {
            src.currentTime = t;
        }
    };

    // ------------------------------------------------------------- verdict
    const toggleMarking = (type) => {
        const vid = videoRef.current;
        if (!vid || readOnly) return;
        const start = pending[type];
        if (start === null) {
            // good light off for this category; the other can mark in parallel
            setPending((p) => ({ ...p, [type]: vid.currentTime }));
        } else {
            // end exactly at the playhead; only a start==end double-press
            // falls back to a single-frame section
            const a = Math.min(start, vid.currentTime);
            let b = Math.max(start, vid.currentTime);
            if (b - a < 1 / fps) b = a + 1 / fps;
            updateAnnotation({
                badRanges: mergeRanges([...badRanges, [a, b, type, severity[type]]]),
            });
            setPending((p) => ({ ...p, [type]: null }));
        }
    };

    const pressGood = () => {
        // good is the default state; pressing it cancels in-progress marks
        setPending({ hands: null, face: null });
    };

    const deleteRange = (index) => {
        updateAnnotation({ badRanges: badRanges.filter((_, i) => i !== index) });
    };

    const resetClip = () => {
        setPending({ hands: null, face: null });
        updateAnnotation({ badRanges: [], reviewed: false, comment: '' });
    };

    const markDone = () => {
        if (!current || readOnly) return;
        setPending({ hands: null, face: null });
        setReviewed(true);
        // the Done save carries the full latest state, superseding any
        // pending debounced save
        clearTimeout(saveTimer.current);
        pendingSave.current = null;
        doPersist(current.kind, current.id, {
            badRanges, reviewed: true, comment,
            duration: videoRef.current ? videoRef.current.duration : current.duration,
        });
        // advance: next unreviewed video after this one, else next in the list
        // in display order (this video hasn't re-sorted yet at this point)
        const idx = sortedVideos.findIndex((v) => `${v.kind}-${v.id}` === currentKey);
        const after = [...sortedVideos.slice(idx + 1), ...sortedVideos.slice(0, idx)];
        const next =
            after.find((v) => !(v.annotation && v.annotation.reviewed)) ||
            sortedVideos[idx + 1];
        if (next) selectVideo(next);
    };

    // ------------------------------------------------------------ keyboard
    useEffect(() => {
        const onKey = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
            if (e.code === 'Space') {
                e.preventDefault();
                togglePlay();
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                step(-1);
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                step(1);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                const vid = videoRef.current;
                if (vid) seekAll(Math.min(vid.duration || 0, vid.currentTime + 1));
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                const vid = videoRef.current;
                if (vid) seekAll(Math.max(0, vid.currentTime - 1));
            } else if (e.key === '[' || e.key === ']' || e.key === 'h') {
                toggleMarking('hands');
            } else if (e.key === 'f') {
                toggleMarking('face');
            } else if (e.key === 'g') {
                pressGood();
            } else if (e.key === 'Enter') {
                e.preventDefault();
                markDone();
            } else if (e.key === '1') {
                applyRate(0.25);
            } else if (e.key === '2') {
                applyRate(0.5);
            } else if (e.key === '3') {
                applyRate(1);
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pending, severity, fps, currentKey, badRanges, reviewed, videos, comment]);

    // ------------------------------------------------------------- helpers
    const statusIcon = (video) => {
        const ann = video.annotation;
        const flagged = ann && ann.bad_ranges && ann.bad_ranges.length > 0;
        return (
            <span className="flex items-center gap-2">
                {ann && ann.hands_issue && (
                    <FontAwesomeIcon icon={faHand} className="text-red-400" title="Hands issue" />
                )}
                {ann && ann.face_issue && (
                    <FontAwesomeIcon icon={faFaceFrown} className="text-red-400" title="Face issue" />
                )}
                {flagged && (
                    <span className="flex items-center gap-1 text-red-500 font-bold">
                        <FontAwesomeIcon icon={faFlag} />
                        {ann.bad_ranges.length}
                    </span>
                )}
                {ann && ann.reviewed ? (
                    <FontAwesomeIcon icon={faCircleCheck} className="text-sil-green-500 text-lg"
                                     title="Reviewed" />
                ) : (
                    <FontAwesomeIcon icon={faCircle} className="text-gray-400 text-xs" />
                )}
            </span>
        );
    };

    const reviewedCount = videos.filter((v) => v.annotation && v.annotation.reviewed).length;
    const dur = duration || (videoRef.current && videoRef.current.duration) || 0;

    // --------------------------------------------------------------- render
    if (loading) {
        return (
            <div className="flex justify-center items-center py-24 text-white text-4xl">
                <FontAwesomeIcon icon={faSpinner} spin />
            </div>
        );
    }

    if (videos.length === 0) {
        return (
            <div className="flex flex-col items-center py-24 text-white gap-4">
                <FontAwesomeIcon icon={faFilm} className="text-6xl opacity-60" />
                <FontAwesomeIcon icon={faWandMagicSparkles} className="text-3xl opacity-60" />
            </div>
        );
    }

    return (
        <div className="flex flex-col lg:flex-row gap-3 w-full">
            {/* ---------------------------------------------------- player */}
            <div className="flex-1 bg-white bg-opacity-10 rounded-lg p-3 min-w-0">
                {/* One pre-rendered side-by-side video when available (sync by
                    construction); otherwise synchronized dual playback.
                    Videos use auto width/height with max constraints so they
                    scale to the LARGEST size that fits — height-driven for
                    portrait clips, width-driven for landscape pairs. */}
                <div className="flex gap-2 justify-center items-start">
                    {isPair && (
                        <div className="relative min-w-0 max-w-[49.5%]">
                            <video
                                ref={srcRef}
                                src={current.source_url}
                                playsInline
                                muted
                                preload="auto"
                                className="block w-auto h-auto max-w-full max-h-[74vh] bg-black rounded-lg"
                                onClick={togglePlay}
                                onWaiting={handleStall}
                                onStalled={handleStall}
                                onCanPlayThrough={handleReady}
                            />
                            <span className="absolute top-2 left-2 w-9 h-9 rounded-full bg-black bg-opacity-60 text-white flex items-center justify-center"
                                  title="Original video">
                                <FontAwesomeIcon icon={faFilm} />
                            </span>
                        </div>
                    )}
                    <div className={`relative min-w-0 ${isPair ? 'max-w-[49.5%]' : 'max-w-full'}`}>
                        <video
                            ref={videoRef}
                            src={current ? (isSxs ? current.sxs_url : current.url) : undefined}
                            playsInline
                            preload="auto"
                            className="block w-auto h-auto max-w-full max-h-[74vh] bg-black rounded-lg"
                            onClick={togglePlay}
                            onPlay={() => { setIsPlaying(true); if (srcRef.current) srcRef.current.play(); }}
                            onPause={() => {
                                setIsPlaying(wantPlay.current && stallPause.current);
                                if (srcRef.current) srcRef.current.pause();
                            }}
                            onEnded={() => { wantPlay.current = false; pauseAll(); }}
                            onWaiting={handleStall}
                            onStalled={handleStall}
                            onCanPlayThrough={handleReady}
                            onTimeUpdate={handleTimeUpdate}
                            onLoadedMetadata={(e) => {
                                setDuration(e.target.duration);
                                e.target.playbackRate = rate;
                                if (srcRef.current) srcRef.current.playbackRate = rate;
                            }}
                        />
                        {isPair && (
                            <span className="absolute top-2 left-2 w-9 h-9 rounded-full bg-black bg-opacity-60 text-white flex items-center justify-center"
                                  title="Anonymized video">
                                <FontAwesomeIcon icon={faWandMagicSparkles} />
                            </span>
                        )}
                        {isSxs && (
                            <>
                                <span className="absolute top-2 left-2 w-9 h-9 rounded-full bg-black bg-opacity-60 text-white flex items-center justify-center"
                                      title="Original video (left)">
                                    <FontAwesomeIcon icon={faFilm} />
                                </span>
                                <span className="absolute top-2 right-2 w-9 h-9 rounded-full bg-black bg-opacity-60 text-white flex items-center justify-center"
                                      title="Anonymized video (right)">
                                    <FontAwesomeIcon icon={faWandMagicSparkles} />
                                </span>
                            </>
                        )}
                    </div>
                </div>

                {/* --------------------------------------------- timeline bar
                    green = good, red = marked bad, pulsing = being marked */}
                <div
                    ref={timelineRef}
                    onPointerDown={handleTimelinePointer}
                    className="relative h-8 mt-3 bg-sil-green-500 bg-opacity-70 rounded-full cursor-pointer select-none touch-none overflow-hidden"
                    title="Timeline — tap to jump"
                >
                    {/* played portion */}
                    <div
                        className="absolute inset-y-0 left-0 bg-white bg-opacity-25"
                        style={{ width: dur ? `${(currentTime / dur) * 100}%` : 0 }}
                    />
                    {/* bad ranges: hands in the top lane, face in the bottom
                        lane, so overlapping sections stay distinguishable */}
                    {dur > 0 &&
                        badRanges.map((r, i) => (
                            <div
                                key={i}
                                className={`absolute ${SEV_BLOCK[rangeSeverity(r)]} ${
                                    rangeType(r) === 'hands' ? 'top-0 h-1/2'
                                    : rangeType(r) === 'face' ? 'bottom-0 h-1/2'
                                    : 'inset-y-0'
                                }`}
                                style={{
                                    left: `${(r[0] / dur) * 100}%`,
                                    width: `${Math.max(0.8, ((r[1] - r[0]) / dur) * 100)}%`,
                                }}
                            />
                        ))}
                    {/* live marking regions (one per category) */}
                    {dur > 0 && ['hands', 'face'].map((type) =>
                        pending[type] !== null ? (
                            <div
                                key={type}
                                className={`absolute ${SEV_BLOCK[severity[type]]} animate-pulse ${
                                    type === 'hands' ? 'top-0 h-1/2' : 'bottom-0 h-1/2'
                                }`}
                                style={{
                                    left: `${(Math.min(pending[type], currentTime) / dur) * 100}%`,
                                    width: `${(Math.abs(currentTime - pending[type]) / dur) * 100}%`,
                                }}
                            />
                        ) : null
                    )}
                    {/* playhead */}
                    <div
                        className="absolute inset-y-0 w-1 bg-white rounded"
                        style={{ left: dur ? `calc(${(currentTime / dur) * 100}% - 2px)` : 0 }}
                    />
                </div>

                {/* time readout — numbers only, universally readable */}
                <div className="flex justify-between text-white font-mono text-sm mt-1 px-1">
                    <span>{formatTime(currentTime)}</span>
                    <span className="opacity-60">{formatTime(dur)}</span>
                </div>

                {/* ------------------------------------------ bad range chips */}
                <div className="flex flex-wrap gap-2 mt-1 min-h-[2.25rem]">
                    {badRanges.map((r, i) => (
                        <span
                            key={i}
                            className={`flex items-center gap-2 border text-white rounded-full px-3 py-1 font-mono text-sm cursor-pointer ${SEV_CHIP[rangeSeverity(r)]}`}
                            onClick={() => seekAll(r[0])}
                        >
                            <FontAwesomeIcon
                                icon={rangeType(r) === 'hands' ? faHand
                                      : rangeType(r) === 'face' ? FACE_ICONS[rangeSeverity(r)]
                                      : faFlag}
                            />
                            {formatTime(r[0])}–{formatTime(r[1])}
                            {!readOnly && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        deleteRange(i);
                                    }}
                                    className="ml-1 w-6 h-6 rounded-full bg-black bg-opacity-30 hover:bg-opacity-60 flex items-center justify-center"
                                    title="Remove this mark"
                                >
                                    <FontAwesomeIcon icon={faXmark} />
                                </button>
                            )}
                        </span>
                    ))}
                </div>

                {/* ------------------------------------------------ controls */}
                <div className="flex items-center justify-center gap-3 mt-2 flex-wrap">
                    <button
                        onClick={() => step(-1)}
                        className="w-14 h-14 rounded-full bg-black bg-opacity-30 hover:bg-opacity-50 text-white text-xl"
                        title="One frame back (←)"
                    >
                        <FontAwesomeIcon icon={faBackwardStep} />
                    </button>
                    <button
                        onClick={togglePlay}
                        className="w-16 h-16 rounded-full bg-white text-sil-red-500 text-2xl shadow-lg hover:scale-105 transition-transform"
                        title="Play / pause (space)"
                    >
                        <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                    </button>
                    <button
                        onClick={() => step(1)}
                        className="w-14 h-14 rounded-full bg-black bg-opacity-30 hover:bg-opacity-50 text-white text-xl"
                        title="One frame forward (→)"
                    >
                        <FontAwesomeIcon icon={faForwardStep} />
                    </button>

                    {/* speed: turtle / walk / rabbit */}
                    <div className="flex gap-1 ml-2 bg-black bg-opacity-30 rounded-full p-1">
                        {SPEEDS.map(({ rate: r, emoji }) => (
                            <button
                                key={r}
                                onClick={() => applyRate(r)}
                                className={`w-12 h-12 rounded-full text-2xl leading-none transition-colors ${
                                    rate === r ? 'bg-sil-yellow-500' : 'hover:bg-white hover:bg-opacity-10'
                                }`}
                                title={`Speed ${r}×`}
                            >
                                {emoji}
                            </button>
                        ))}
                    </div>

                    <span className="w-6" />

                    {/* read-only (admin Results): show the reviewer's status and
                        comment instead of the editing controls */}
                    {readOnly && (
                        <div className="flex items-center gap-3 text-white">
                            <span className={`flex items-center gap-2 px-4 h-11 rounded-full ${
                                reviewed ? 'bg-sil-green-500 bg-opacity-60' : 'bg-black bg-opacity-30 text-opacity-60'
                            }`}>
                                <FontAwesomeIcon icon={reviewed ? faCircleCheck : faCircle} />
                                <span className="text-sm font-semibold">{reviewed ? 'Done' : 'Not done'}</span>
                            </span>
                            {comment && (
                                <span className="flex items-center gap-2 px-4 h-11 rounded-full bg-black bg-opacity-30 max-w-md">
                                    <FontAwesomeIcon icon={faComment} className="text-white text-opacity-50" />
                                    <span className="text-sm truncate" title={comment}>{comment}</span>
                                </span>
                            )}
                        </div>
                    )}

                    {!readOnly && (<>
                    {/* good — lit by default; goes dark while marking a bad section */}
                    <button
                        onClick={pressGood}
                        className={`flex items-center justify-center h-14 w-20 rounded-full text-white text-2xl shadow-lg transition-all ${
                            !marking
                                ? 'bg-sil-green-400 ring-4 ring-sil-green-700'
                                : 'bg-sil-green-500 bg-opacity-30 hover:bg-opacity-60'
                        }`}
                        title={marking ? 'Cancel marking — back to good' : 'Video is good (default)'}
                    >
                        <FontAwesomeIcon icon={faThumbsUp} />
                    </button>

                    {/* category flags: press at the start of a bad section,
                        again at its end — hands and face mark independently
                        and may both run at once. The carets pick the badness
                        level: up = milder (orange), down = worse (dark red). */}
                    {[
                        { type: 'hands', label: 'hands (h)' },
                        { type: 'face', label: 'face (f)' },
                    ].map(({ type, label }) => {
                        const sev = severity[type];
                        const icon = type === 'hands' ? faHand : FACE_ICONS[sev];
                        const bumpSeverity = (e, delta) => {
                            e.stopPropagation();
                            setSeverity((s) => ({
                                ...s,
                                [type]: Math.min(3, Math.max(1, s[type] + delta)),
                            }));
                        };
                        return (
                            <div
                                key={type}
                                onClick={() => toggleMarking(type)}
                                role="button"
                                className={`flex items-center justify-center gap-2 h-14 pl-5 pr-2 rounded-full text-white text-2xl shadow-lg transition-all cursor-pointer select-none ${
                                    pending[type] !== null
                                        ? `${SEV_MARKING[sev]} animate-pulse ring-4 ring-red-300`
                                        : SEV_BUTTON[sev]
                                }`}
                                title={pending[type] !== null
                                    ? `Press to finish the bad ${label} section`
                                    : `Press where a bad ${label} section starts`}
                            >
                                <FontAwesomeIcon icon={faFlag} className="text-lg" />
                                <FontAwesomeIcon icon={icon} />
                                {pending[type] !== null && (
                                    <span className="font-mono text-sm">{formatTime(pending[type])} …</span>
                                )}
                                {/* severity carets: up = milder, down = worse */}
                                <span className="flex flex-col justify-center w-6 leading-none ml-1">
                                    <button
                                        onClick={(e) => bumpSeverity(e, -1)}
                                        className={`text-base leading-none hover:scale-125 transition-transform ${
                                            sev > 1 ? '' : 'invisible'
                                        }`}
                                        title="Less bad"
                                        tabIndex={-1}
                                    >
                                        <FontAwesomeIcon icon={faCaretUp} />
                                    </button>
                                    <button
                                        onClick={(e) => bumpSeverity(e, 1)}
                                        className={`text-base leading-none hover:scale-125 transition-transform ${
                                            sev < 3 ? '' : 'invisible'
                                        }`}
                                        title="Worse"
                                        tabIndex={-1}
                                    >
                                        <FontAwesomeIcon icon={faCaretDown} />
                                    </button>
                                </span>
                            </div>
                        );
                    })}

                    {/* reset this clip */}
                    <button
                        onClick={resetClip}
                        className="w-14 h-14 rounded-full bg-black bg-opacity-30 hover:bg-opacity-50 text-white text-xl"
                        title="Clear all marks on this video"
                    >
                        <FontAwesomeIcon icon={faRotateLeft} />
                    </button>

                    {/* done -> next video */}
                    <button
                        onClick={markDone}
                        className="flex items-center justify-center h-14 px-8 rounded-full bg-sil-blue-500 hover:bg-sil-blue-600 text-white text-2xl shadow-lg transition-all"
                        title="Done — save and go to the next video (Enter)"
                    >
                        <FontAwesomeIcon icon={faCheck} />
                    </button>

                    {/* optional free comment */}
                    <div className="w-52 flex items-center gap-2 bg-black bg-opacity-30 rounded-full px-4 h-11">
                        <FontAwesomeIcon icon={faComment} className="text-white text-opacity-50" />
                        <input
                            type="text"
                            value={comment}
                            onChange={(e) => updateAnnotation({ comment: e.target.value })}
                            placeholder="…"
                            maxLength={2000}
                            className="flex-1 min-w-0 bg-transparent text-white placeholder-white placeholder-opacity-40 outline-none text-sm"
                            title="Optional comment"
                        />
                    </div>

                    {/* save indicator (saving happens automatically) */}
                    <span className="w-8 text-center text-xl" title="Changes save automatically">
                        {saveState === 'saving' && (
                            <FontAwesomeIcon icon={faCloudArrowUp} className="text-white animate-pulse" />
                        )}
                        {saveState === 'saved' && (
                            <FontAwesomeIcon icon={faCheck} className="text-sil-green-500" />
                        )}
                        {saveState === 'error' && (
                            <FontAwesomeIcon icon={faXmark} className="text-red-500" />
                        )}
                    </span>
                    </>)}
                </div>
            </div>

            {/* ------------------------------------------------ video list */}
            <div className="lg:w-64 flex-shrink-0 bg-white bg-opacity-10 rounded-lg p-3 max-h-[85vh] overflow-y-auto">
                {/* visual progress: reviewed / total */}
                <div className="flex items-center gap-2 mb-3 text-white">
                    <FontAwesomeIcon icon={faCircleCheck} className="text-sil-green-500" />
                    <div className="flex-1 bg-black bg-opacity-30 rounded-full h-3">
                        <div
                            className="bg-sil-green-500 h-3 rounded-full transition-all"
                            style={{ width: `${videos.length ? (reviewedCount / videos.length) * 100 : 0}%` }}
                        />
                    </div>
                    <span className="font-mono text-sm">{reviewedCount}/{videos.length}</span>
                </div>
                {sortedVideos.map((video) => {
                    const key = `${video.kind}-${video.id}`;
                    const active = key === currentKey;
                    return (
                        <button
                            key={key}
                            onClick={() => selectVideo(video)}
                            className={`w-full text-left flex items-center gap-2 p-3 mb-1 rounded-lg transition-colors ${
                                active
                                    ? 'bg-sil-yellow-500 bg-opacity-30 ring-2 ring-sil-yellow-500'
                                    : 'bg-black bg-opacity-20 hover:bg-opacity-40'
                            }`}
                        >
                            <FontAwesomeIcon
                                icon={video.kind === 'processed' ? faWandMagicSparkles : faFilm}
                                className="text-white opacity-70"
                                title={video.kind === 'processed' ? 'Generated video' : 'Uploaded video'}
                            />
                            <span className="flex-1 min-w-0 text-white text-sm leading-tight">
                                <span className="break-all">{video.filename}</span>
                                {video.mine === false && video.owner && (
                                    <span className="block text-xs text-white text-opacity-50 break-all">
                                        {video.owner}
                                    </span>
                                )}
                            </span>
                            {video.mine && !video.shared && (
                                <FontAwesomeIcon icon={faLock}
                                                 className="text-white opacity-50 text-xs"
                                                 title="Private — only you see this video" />
                            )}
                            {statusIcon(video)}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default AnnotationTab;
